'use client';

import { __spreadArray, __assign, __awaiter, __generator } from '../_virtual/_tslib.js';
import React__default, { useState, useRef, useEffect, useCallback } from 'react';
import { AutoCompleteContainer, InputRecommend, ListRecommend, RecommendItem } from './styles.js';
import useDebounce from '../hooks/useDebounce.js';
import useFloating from '../hooks/useFloating/index.js';
import useClickAway from '../hooks/useClickAway.js';
import '../hooks/useMakeSearchable/types.js';
import '../Utils/constants.js';
import '../services/resumes/types.js';
import useDropdownAnimation from '../hooks/useDropdownAnimation.js';
import useKeyboardAccessibility from '../hooks/useKeyboardAccessibility.js';
import { getOptions } from './helper.js';

function AutoComplete(_a) {
    var options = _a.options, renderInput = _a.renderInput, _b = _a.fieldKey, fieldKey = _b === void 0 ? 'id' : _b, _c = _a.fieldValue, fieldValue = _c === void 0 ? 'name' : _c, _d = _a.value, value = _d === void 0 ? null : _d, onChange = _a.onChange, _e = _a.multiple, multiple = _e === void 0 ? false : _e, _f = _a.debounce, debounce = _f === void 0 ? 200 : _f, _g = _a.clearKeywordsAfterChange, clearKeywordsAfterChange = _g === void 0 ? true : _g, _h = _a.creatable, creatable = _h === void 0 ? {
        enable: false,
        defaultKey: -1
    } : _h;
    /*
     * State and hooks
     * */
    var _j = useState([]), selectedItems = _j[0], setSelectedItems = _j[1];
    var _k = useState(''), keywords = _k[0], setKeywords = _k[1];
    var debouncedKeywords = useDebounce(keywords, debounce, true);
    var _l = useState([]), optionsState = _l[0], setOptionsState = _l[1];
    var _m = useState(false), showRecommend = _m[0], setShowRecommend = _m[1];
    var dropdownRef = useRef(null);
    /**
     * Effects
     * */
    // Get options
    useEffect(function () {
        function getOptionsPromise() {
            return __awaiter(this, void 0, void 0, function () {
                var response;
                return __generator(this, function (_a) {
                    switch (_a.label) {
                        case 0: return [4 /*yield*/, getOptions(options, debouncedKeywords, fieldValue, selectedItems, fieldKey)];
                        case 1:
                            response = _a.sent();
                            setOptionsState(response);
                            return [2 /*return*/];
                    }
                });
            });
        }
        getOptionsPromise();
    }, [options, debouncedKeywords, selectedItems]);
    // Get selected items
    useEffect(function () {
        if (value !== null) {
            var convertedValue = Array.isArray(value) ? value : [value];
            setSelectedItems(convertedValue);
        }
        else {
            setSelectedItems([]);
        }
    }, [value]);
    /**
     * Handle logics and actions
     * */
    var _o = useFloating({
        placement: 'bottom-start',
        position: 'fixed',
        config: {
            useWindowAsFlippingContainer: true
        }
    }), context = _o.context, refs = _o.refs, FloatingPortal = _o.FloatingPortal;
    var handleChange = useCallback(function (option, canSelect) {
        if (!canSelect) {
            return;
        }
        var changedValues;
        if (multiple) {
            changedValues = __spreadArray([], selectedItems, true);
            changedValues.push(option);
            if (clearKeywordsAfterChange) {
                setKeywords('');
            }
        }
        else {
            changedValues = option;
            setKeywords(option[fieldValue]);
        }
        onChange(changedValues);
    }, [onChange, selectedItems]);
    // Keyboard navigation
    var activeIndex = useKeyboardAccessibility(showRecommend && optionsState.length > 0, {
        onClickEscape: function () { return setShowRecommend(false); },
        optionsLength: optionsState.length,
        target: dropdownRef,
        resetIndex: true,
        forceResetIndexDeps: [keywords === '', !showRecommend],
        onSelelected: function (index) {
            var valueIds = selectedItems.map(function (item) { return item[fieldKey]; });
            var isActive = valueIds.includes(optionsState[index][fieldKey]) && !creatable.enable;
            var canSelect = !creatable.enable
                ? !isActive
                : !selectedItems.some(function (item) { return item[fieldValue] === optionsState[index][fieldValue]; });
            handleChange(optionsState[index], canSelect);
        }
    }).activeIndex;
    useClickAway(function () {
        setShowRecommend(false);
    }, {
        nodeRefs: [refs.popup, refs.reference]
    });
    var _p = useDropdownAnimation(showRecommend && optionsState.length > 0, 'bottom-start'), isMounted = _p.isMounted, styles = _p.styles;
    /**
     * Render components
     * */
    var handleInputFocus = function () {
        setShowRecommend(true);
    };
    var handleDelete = useCallback(function (option) {
        if (multiple) {
            var filtedValues = __spreadArray([], selectedItems, true);
            if (creatable.enable && option[fieldKey] === creatable.defaultKey) {
                filtedValues = filtedValues.filter(function (item) { return item[fieldValue] !== option[fieldValue]; });
            }
            else {
                filtedValues = filtedValues.filter(function (item) { return item[fieldKey] !== option[fieldKey]; });
            }
            onChange(filtedValues);
        }
    }, [multiple, selectedItems, creatable.enable, creatable.defaultKey, fieldKey, onChange, fieldValue]);
    var handleChangeKeywords = useCallback(function (value) {
        if (!multiple && value === '') {
            onChange(null);
        }
        setKeywords(value);
    }, [onChange]);
    var handleKeyDown = useCallback(function (event) {
        if (multiple) {
            if (event.key === 'Backspace' || event.keyCode === 8) {
                if (keywords.length === 0 && selectedItems.length > 0) {
                    var changedItems = __spreadArray([], selectedItems, true);
                    changedItems.pop();
                    onChange(changedItems);
                }
            }
        }
    }, [keywords, selectedItems, onChange]);
    var input = renderInput({
        value: keywords,
        onChange: handleChangeKeywords,
        onFocus: handleInputFocus,
        onDelete: handleDelete,
        onKeyDown: handleKeyDown,
        autoComplete: 'off',
        'aria-autocomplete': 'list',
        autoCapitalize: 'off',
        autoCorrect: 'off',
        spellCheck: 'false'
    });
    return (React__default.createElement(AutoCompleteContainer, null,
        React__default.createElement("div", { ref: refs.setReference }, input),
        isMounted ? (React__default.createElement(FloatingPortal, { enable: true },
            React__default.createElement(InputRecommend, { ref: refs.setPopup, style: __assign({ position: context.position, top: context.y, left: context.x, width: refs.reference.current.offsetWidth, zIndex: 1100 }, styles) },
                React__default.createElement(ListRecommend, { ref: dropdownRef }, optionsState.map(function (option, index) {
                    var valueIds = selectedItems.map(function (item) { return item[fieldKey]; });
                    var isActive = valueIds.includes(option[fieldKey]) && !creatable.enable;
                    var canSelect = !creatable.enable
                        ? !isActive
                        : !selectedItems.some(function (item) { return item[fieldValue] === option[fieldValue]; });
                    return (React__default.createElement(RecommendItem, { isActive: isActive, isHover: index === activeIndex, key: option[fieldKey], onClick: function () {
                            handleChange(option, canSelect);
                            setShowRecommend(false);
                        } }, option[fieldValue]));
                }))))) : null));
}

export { AutoComplete as default };
//# sourceMappingURL=index.js.map
